import React from 'react';
import { motion } from 'framer-motion';
import { Users, Mail, Phone, Calendar, Clock, UserCheck, UserX, X } from 'lucide-react';
import { UserDetail } from '../../../types/role';
import { formatDistanceToNow } from 'date-fns';
import RightDrawer from '../../../shared/components/form/RightDrawer';

interface UserDetailsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  roleName: string;
  userCount: number;
  userDetails: UserDetail[];
}

const UserDetailsPanel: React.FC<UserDetailsPanelProps> = ({
  isOpen,
  onClose,
  roleName,
  userCount,
  userDetails
}) => {
  const getStatusIcon = (status: string) => {
    return status === 'active' ? (
      <UserCheck size={16} className="text-green-600" />
    ) : (
      <UserX size={16} className="text-red-600" />
    );
  };

  const getStatusColor = (status: string) => {
    return status === 'active' 
      ? 'bg-green-100 text-green-800 border-green-200' 
      : 'bg-red-100 text-red-800 border-red-200';
  };

  return (
    <RightDrawer
      open={isOpen}
      onClose={onClose}
      size="lg"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6 p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 -mt-6 -mx-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Users size={20} className="text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{roleName}</h2>
            <p className="text-sm text-gray-600">{userCount} user{userCount !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors duration-200"
          aria-label="Close drawer"
        >
          <X size={20} />
        </button>
      </div>

      {/* Content */}
      <div className="space-y-4">
        {userCount === 0 ? (
          <div className="text-center py-12">
            <Users size={48} className="text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No users assigned</h3>
            <p className="text-sm text-gray-500">This role doesn't have any users assigned yet.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {userDetails.map((user) => (
              <motion.div
                key={user._id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-blue-300 transition-colors duration-200"
              >
                {/* User Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-semibold text-blue-600">
                        {user.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">{user.name}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        {getStatusIcon(user.status)}
                        <span className={`text-xs px-2 py-1 rounded-full border ${getStatusColor(user.status)}`}>
                          {user.status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* User Details */}
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Mail size={14} />
                    <span>{user.email}</span>
                  </div>
                  
                  {user.mobile && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone size={14} />
                      <span>{user.mobile}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 text-gray-600">
                    <Calendar size={14} />
                    <span>Joined {formatDistanceToNow(new Date(user.createdAt), { addSuffix: true })}</span>
                  </div>
                  
                  {user.lastLoginAt && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock size={14} />
                      <span>Last login {formatDistanceToNow(new Date(user.lastLoginAt), { addSuffix: true })}</span>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </RightDrawer>
  );
};

export default UserDetailsPanel;
